import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { AddTournamentUserToTeam, FetchUserEvents } from '../interfaces/actions';
import {
  AddToTeamResponseDto,
  DeleteUserResponseDto,
  FetchUsersEventsResponseDto,
  GetPlayersByTournamentResponseDto,
  GetUserResponseDto,
  KickFromTeamResponseDto,
  UpsertPlayerResponseDto,
} from '../interfaces/dto';

import { Player } from '../interfaces/player';
import { UpsertTournamentApplicationUserDto } from '../interfaces/upsert-player-input.dto';

/**
 * Единственный репозиторий игроков. Раньше рядом жил второй класс с тем же именем —
 * `modules/teams/domain/repositories/players.repository.ts`, — дублировавший шесть
 * операций (`user`, `upsertTournamentUser`, `addToTeam`, `kickFromTeam`,
 * `deleteTournamentUser` и подсказки по `tournament(id).users`) с более бедной
 * выборкой полей. Модуль `teams` теперь вызывает этот.
 */
export class PlayersRepository {
  constructor(private readonly graphql: GraphqlResource) {}

  /**
   * Подсказки при добавлении игрока в состав команды: та же коллекция
   * `tournament(id).users`, что и в списке игроков, но только заведённые вручную
   * (`isReal === false`) — реальных пользователей в состав так не добавляют.
   */
  public async suggestTournamentPlayers(tournamentId: number, nameFilter: string): Promise<Player[]> {
    const query = `#graphql
      query suggestTournamentPlayers($id: Int!, $nameFilter: String) {
        tournament(id: $id) {
          users(limit: 20, offset: 0, nameFilter: $nameFilter) {
            id
            isReal
            name
            surname
            middleName
            birthDate
            tournamentAvatars {
              id
              tournament {
                id
              }
              image {
                externalUrl
              }
            }
            photo {
              externalUrl
            }
          }
        }
      }
    `;

    const response = await this.graphql.query<GetPlayersByTournamentResponseDto>(query, {
      id: tournamentId,
      nameFilter,
    });

    return response.data.data.tournament.users.filter((user) => !user.isReal);
  }

  public async upsertTournamentApplicationForUser(params: UpsertTournamentApplicationUserDto, tournamentId: number): Promise<Player> {
    const query = `#graphql
      mutation upsertTournamentUser($input: CreateUserInput!, $tournamentId: Int!) {
        upsertTournamentUser(input: $input, tournamentId: $tournamentId) {
          id
          name
          surname
          middleName
          birthDate
          weight
          height
          tournamentAvatars {
            id
            tournament {
              id
            }
            image {
              externalUrl
            }
          }
          photo {
            externalUrl
          }
          positionsInTeams {
            position
            number
            team {
              id
              name
              createdAt
              emblem {
                externalUrl
              }
            }
          }
        }
      }
    `;

    const variables = {
      input: {
        ...params,
      },
      tournamentId,
    };
    const response = await this.graphql.query<UpsertPlayerResponseDto>(query, variables);

    // Ошибку обязательно пробрасываем: на ней держится ветка «игрок уже заведён»
    // в modules/teams — там при провале подставляют переданный userId.
    if (response.data.errors) {
      throw new Error(response.data.errors[0].message);
    }

    const { upsertTournamentUser } = response.data.data;
    return {
      ...upsertTournamentUser,
    };
  }

  public async getUser(id: number): Promise<Player> {
    const query = `#graphql
      query user($id: Int!) {
        user(id: $id) {
          id
          name
          surname
          middleName
          birthDate
          weight
          height
          isReal
          tournamentAvatars {
            id
            tournament {
              id
            }
            image {
              externalUrl
            }
          }
          photo {
            externalUrl
          }
          positionsInTeams {
            position
            number
            team {
              id
              name
              createdAt
              emblem {
                externalUrl
              }
            }
          }
        }
      }
    `;
    const response = await this.graphql.query<GetUserResponseDto>(query, {
      id,
    });

    const { user } = response.data.data;
    return user;
  }

  async deleteTournamentUser(payload: { userId: number }) {
    const query = `
      mutation deleteTournamentUser($id: Int!) {
        deleteTournamentUser(userId: $id) {
            status
        }
      }
    `;

    const response = await this.graphql.query<DeleteUserResponseDto>(query, {
      id: payload.userId,
    });

    return response.data.data.deleteTournamentUser.status;
  }

  async addTournamentUserToTeam(payload: AddTournamentUserToTeam): Promise<Player> {
    const query = /* GraphQL */ `
      mutation addToTeam($userId: Int!, $teamId: Int!) {
        addToTeam(input: { teamId: $teamId, userId: $userId }) {
          id
          name
          surname
          middleName
          birthDate
          weight
          height
          tournamentAvatars {
            id
            tournament {
              id
            }
            image {
              externalUrl
            }
          }
          photo {
            externalUrl
          }
          positionsInTeams {
            position
            number
            team {
              id
              name
              createdAt
              emblem {
                externalUrl
              }
            }
          }
        }
      }
    `;

    const response = await this.graphql.query<AddToTeamResponseDto>(query, {
      userId: payload.userId,
      teamId: payload.teamId,
    });

    if (response.data.errors) {
      throw new Error(response.data.errors[0].message);
    }

    return response.data.data.addToTeam;
  }

  async kickTournamentUserFromTeam(payload: { userId: number; teamId: number }) {
    const query = `
      mutation kickFromTeam($userId: Int!, $teamId: Int!) {
         kickFromTeam(teamId: $teamId, userId: $userId)
      }
    `;

    const response = await this.graphql.query<KickFromTeamResponseDto>(query, {
      userId: payload.userId,
      teamId: payload.teamId,
    });

    return !response.data.errors;
  }

  async fetchUsersEvents(props: FetchUserEvents) {
    const query = `
      #graphql
      query UserEvents($userId: Int!, $limit: Int, $offset: Int) {
        userEvents(userId: $userId, limit: $limit, offset: $offset) {
          videoUrl
          match {
            id
            date
            teamMatches {
              id
              team {
                id
                name
              }
              score
            }
            championship {
              id
              name
            }
          }
          second
          id
          period
          type
        }
      }
    `;

    const variable: FetchUserEvents = { userId: props.userId };

    if (props.limit) {
      variable['limit'] = props.limit;
    }

    if (props.offset) {
      variable['offset'] = props.offset;
    }

    if (props.type) {
      variable['type'] = props.type;
    }

    const response = await this.graphql.query<FetchUsersEventsResponseDto>(query, variable);

    if (response.data.errors) {
      throw new Error(response.data.errors[0].message);
    }

    return response.data.data.userEvents;
  }
}

const playersRepository = new PlayersRepository(graphqlResource);

export default playersRepository;
